import { v4 as uuid } from 'uuid'

import {
  Contacts,
  ContentStateInterface,
  GeoCoordinates,
  InsertionIdentification,
  Salutation,
  SaveType,
  User,
} from '../../../@types/types'
import { InsertionState } from '../../../@types/state-types'
import { emptyValidationMassages } from '../../../services/validation/constants/emptyValidationMessages'
import { getContactsLoadSuccess } from '../contact/contactFeature'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ValidationResult } from '../../../@types/validation-types'

export function newInsertionIdentification(): InsertionIdentification {
  return {
    id: uuid(),
    realEstateId: undefined,
    progressState: undefined,
    metaInformation: {
      entryMode: 'NEW_INSERTION',
    },
  }
}

export const initialState: InsertionState = {
  save: SaveType.NOT_SAVED,
  changed: false,
  changeVersion: 0,
  saveRequestedVersion: undefined,
  insertionIdentification: newInsertionIdentification(),
  insertionData: {},
  validationResult: emptyValidationMassages,
  customValidationResults: {
    objectNumbers: {
      validated: false,
      fields: {
        externalId: [],
      },
    },
  },
}

const insertionFeatureSlice = createSlice({
  name: 'insertionFeature',
  initialState,
  selectors: {
    useIsSaved: (state) => state.save === SaveType.SAVED || state.save === SaveType.SAVING,
  },
  reducers: {
    insertionSave(
      state,
      action: PayloadAction<{ insertionData: ContentStateInterface; validationResult: ValidationResult }>
    ) {
      const { insertionData, validationResult } = action.payload
      state.insertionData = insertionData
      state.validationResult = validationResult
    },
    validationSave: (state, action: PayloadAction<{ validationResult: ValidationResult }>) => {
      state.validationResult = action.payload.validationResult
    },
    insertionUpdateSuccess: (state) => {
      state.save = SaveType.SAVED
      state.changed = state.changeVersion > (state.saveRequestedVersion ?? 0)
      state.saveRequestedVersion = undefined
    },
    insertionUpdateRequest: (state) => {
      state.save = SaveType.SAVING
      state.saveRequestedVersion = state.changeVersion
    },
    insertionUpdateError: (state) => {
      state.save = SaveType.FAILED_TO_SAVE
      state.saveRequestedVersion = undefined
    },
    updateInsertionId: (
      state,
      action: PayloadAction<{
        externalId: string
        realEstateId: number
        id: string
        progressState: string
        unitId?: string
        buildingId?: string
      }>
    ) => {
      const { externalId, realEstateId, id, progressState, unitId, buildingId } = action.payload
      state.insertionData.externalId = externalId
      state.insertionIdentification.unitId = unitId
      state.insertionIdentification.buildingId = buildingId
      state.insertionIdentification.id = id
      state.insertionIdentification.realEstateId = realEstateId
      state.insertionIdentification.progressState = progressState
    },
    getUserInitialCompletionSuccess: (state, action: PayloadAction<{ user: User; prefillPhoneNumber?: boolean }>) => {
      const { user, prefillPhoneNumber = true } = action.payload
      if (
        (state.insertionData.salutation && state.insertionData.salutation !== Salutation.NO_SALUTATION) ||
        state.insertionData.phoneNumber ||
        state.insertionData.email ||
        state.insertionData.firstName ||
        state.insertionData.lastName ||
        ((!user.contact?.salutationType || user.contact?.salutationType === Salutation.NO_SALUTATION) &&
          !user.contact?.phoneNumber &&
          !user.contact?.email &&
          !user.contact?.firstName &&
          !user.contact?.lastName &&
          !user.contingentCustomer)
      ) {
        return
      }
      state.insertionData.salutation = user.contact.salutationType
      state.insertionData.phoneNumber = prefillPhoneNumber ? user.contact.phoneNumber : undefined
      state.insertionData.email = user.contact.email
      state.insertionData.firstName = user.contact.firstName
      state.insertionData.lastName = user.contact.lastName
    },
    getInsertionInitialCompletionSuccess: (
      state,
      action: PayloadAction<{
        insertionData: ContentStateInterface
        insertionIdentification: InsertionIdentification
        geoCoordinates?: GeoCoordinates
      }>
    ) => {
      const { insertionData: insertionInitialCompletion, insertionIdentification, geoCoordinates } = action.payload
      state.save = SaveType.SAVED
      state.changed = false
      state.changeVersion = initialState.changeVersion
      state.saveRequestedVersion = initialState.saveRequestedVersion
      state.insertionData = {
        ...insertionInitialCompletion,
        manualQuarter: {
          ...insertionInitialCompletion.manualQuarter!,
          geoCoordinates: geoCoordinates,
        },
      }
      state.insertionIdentification = { ...insertionIdentification }
    },
    getInsertionEmptyInitialCompletionSuccess: (
      state,
      action: PayloadAction<{ insertionData: ContentStateInterface }>
    ) => {
      const { insertionData: insertionInitialCompletion } = action.payload
      state.insertionData = {
        ...state.insertionData,
        ...insertionInitialCompletion,
        firstName: state.insertionData.firstName || insertionInitialCompletion.firstName,
        lastName: state.insertionData.lastName || insertionInitialCompletion.lastName,
        salutation: state.insertionData.salutation || insertionInitialCompletion.salutation,
        email: state.insertionData.email || insertionInitialCompletion.email,
        phoneNumber: state.insertionData.phoneNumber || insertionInitialCompletion.phoneNumber,
      }
    },
    insertionChanged: (state) => {
      state.changed = true
      state.changeVersion += 1
    },
    resetInsertion: (state) => {
      state.save = initialState.save
      state.changed = initialState.changed
      state.changeVersion = initialState.changeVersion
      state.saveRequestedVersion = initialState.saveRequestedVersion
      state.insertionIdentification = initialState.insertionIdentification
      state.insertionData = {
        ...initialState.insertionData,
        firstName: state.insertionData.firstName,
        lastName: state.insertionData.lastName,
        salutation: state.insertionData.salutation,
        email: state.insertionData.email,
        phoneNumber: state.insertionData.phoneNumber,
      }
    },
    createNewInsertion: (state) => {
      state.save = initialState.save
      state.changed = initialState.changed
      state.changeVersion = initialState.changeVersion
      state.saveRequestedVersion = initialState.saveRequestedVersion
      state.insertionIdentification = newInsertionIdentification()
      state.insertionData = {
        ...initialState.insertionData,
        firstName: state.insertionData.firstName,
        lastName: state.insertionData.lastName,
        salutation: state.insertionData.salutation,
        email: state.insertionData.email,
        phoneNumber: state.insertionData.phoneNumber,
      }
    },
    getObjectInitialCompletionSuccess: (
      state,
      action: PayloadAction<{
        insertionData: ContentStateInterface
        insertionIdentification: InsertionIdentification
      }>
    ) => {
      const { insertionData: insertionInitialCompletion, insertionIdentification } = action.payload
      state.save = SaveType.NOT_SAVED
      state.changed = initialState.changed
      state.changeVersion = initialState.changeVersion
      state.saveRequestedVersion = initialState.saveRequestedVersion
      state.insertionData = {
        ...insertionInitialCompletion,
        firstName: state.insertionData.firstName,
        lastName: state.insertionData.lastName,
        salutation: state.insertionData.salutation,
        email: state.insertionData.email,
        phoneNumber: state.insertionData.phoneNumber,
      }
      state.insertionIdentification = { ...insertionIdentification }
    },
    updateExternalIdValidationMessage: (state, action: PayloadAction<{ validationMessages: string[] }>) => {
      const { fields } = state.customValidationResults.objectNumbers
      state.customValidationResults.objectNumbers.validated = true
      fields!.externalId = action.payload.validationMessages
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getContactsLoadSuccess, (state, action: PayloadAction<{ contacts: Contacts }>) => {
      const hasNewContactToSelect = state.insertionData.contactId === undefined && action.payload.contacts.length > 0
      if (hasNewContactToSelect) {
        state.changed = true
        state.changeVersion += 1
        state.save = SaveType.NOT_SAVED
        state.insertionData = { ...initialState.insertionData, contactId: action.payload.contacts[0].contactId }
      }
    })
  },
})

export const {
  insertionSave,
  validationSave,
  insertionUpdateSuccess,
  insertionUpdateRequest,
  insertionUpdateError,
  updateInsertionId,
  getUserInitialCompletionSuccess,
  getInsertionInitialCompletionSuccess,
  getInsertionEmptyInitialCompletionSuccess,
  insertionChanged,
  resetInsertion,
  createNewInsertion,
  getObjectInitialCompletionSuccess,
  updateExternalIdValidationMessage,
} = insertionFeatureSlice.actions

export const { useIsSaved } = insertionFeatureSlice.selectors
export default insertionFeatureSlice.reducer
